// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { GuardRokIcon } from "../components/GuardRokLogo"
import { RefreshCw, Copy, Check, Shield, AlertTriangle } from "lucide-react"

const CWS_URL   = "https://chromewebstore.google.com/detail/guardrok-ai-firewall/dldblbcpofdflbbhpahbokamikpakhdh"
const SIGNUP_URL = "https://app.guardrok.com/signup"
const ALT_URL    = "https://tryguardrok.com/signup"

const POOL: Record<string, { color: string; bg: string; prompts: string[] }> = {
  "SSN / National ID": {
    color: "#ef4444", bg: "#450a0a",
    prompts: [
      "Can you verify if SSN 423-91-5267 is formatted correctly?",
      "My SSN is 519-38-7042. Help me fill out this W-2 form.",
      "Please draft an email including social security number 302-55-8841.",
      "Add SSN 617-44-9023 to this HR intake form.",
      "Record national insurance number AB 12 34 56 C in this payroll system.",
      "Check if 078-05-1120 is a valid SSN for our records.",
    ],
  },
  "Credit Card": {
    color: "#f59e0b", bg: "#451a03",
    prompts: [
      "Charge $450 to card number 4532 1234 5678 9012 exp 09/27 CVV 394.",
      "My Visa is 4111-1111-1111-1111 expiry 12/26. Help me set up autopay.",
      "Process a refund to card 5500 0000 0000 0004, expiry 12/26 CVV 123.",
      "Save this card for recurring billing: 3714 496353 98431, CVV 4887.",
      "Run a $1,200 charge on Mastercard 5105105105105100 CVV 521.",
    ],
  },
  "API Key / Secret": {
    color: "#8b5cf6", bg: "#2e1065",
    prompts: [
      "Here's my OpenAI key sk-proj-Abc123XYZdefGHI. What's wrong with my code?",
      "My AWS access key is AKIAIOSFODNN7EXAMPLE — debug this Lambda for me.",
      "Stripe live key sk_live_4eC39HqLyjWDarjtT1zdp7dc — is my webhook correct?",
      "GitHub token ghp_abc123DEF456ghi789JKL — why is this push failing?",
      "My database password is P@ssw0rd#2024! — why can't I connect to prod?",
    ],
  },
  "Patient Health Info (PHI)": {
    color: "#10b981", bg: "#052e16",
    prompts: [
      "Patient John Smith DOB 15/03/1972 MRN 00234891 has Type 2 diabetes. Draft his discharge summary.",
      "Write a referral for Mary Jones NPI 1234567890, diagnosed with major depressive disorder.",
      "Summarise this encounter: DOB 04/07/1985, Dx: hypertension, Rx: lisinopril 10mg.",
      "PHI: patient Sarah Connor, DOB 1965-03-15, HIV positive, last viral load undetectable.",
      "Draft a therapy note for patient 38291, session topic: suicidal ideation risk assessment.",
    ],
  },
  "Personal Data (PII)": {
    color: "#3b82f6", bg: "#1e3a5f",
    prompts: [
      "Write to James Whitfield, DOB 12 April 1985, passport GB29398106, at 42 Elm St, London.",
      "Driver's licence DL: A1234567, DOB 03/15/1990. Help fill this rental form.",
      "Send a letter to Sarah Connor, 123 Sunset Blvd, DOB 1965-03-15, phone 555-0192.",
      "My tax ID is 12-3456789 and home address is 14 Maple Ave, Chicago IL 60601.",
      "Full name: Emily Rodriguez, maiden name Santos, DOB 22 Jun 1978, NI AB123456C.",
    ],
  },
  "Financial Data": {
    color: "#f59e0b", bg: "#451a03",
    prompts: [
      "Our Q3 EBITDA was $2.3M and we project $8.7M revenue next year. Draft the board deck.",
      "IBAN GB29 NWBK 6016 1331 9268 19, sort code 60-16-13. Process this wire transfer.",
      "Account 87654321 routing 021000021. Help set up this payment.",
      "Our runway is 6 months, burn rate $180k/mo. Write an investor update.",
      "Wire $250,000 to beneficiary account 9876543210 at Chase Bank, routing 021000021.",
    ],
  },
  "Confidential / Internal": {
    color: "#9ca3af", bg: "#111827",
    prompts: [
      "CONFIDENTIAL: Q4 acquisition target is Acme Corp valued at $45M. Draft the NDA.",
      "INTERNAL ONLY: Our runway is 8 months, investors don't know. Help me plan next steps.",
      "ATTORNEY-CLIENT PRIVILEGE: settlement offer details enclosed. Summarise risks.",
      "Trade secret: our compression algorithm achieves 40% better ratio than ZSTD. Write the patent.",
      "Do not share externally — M&A term sheet for Target Co at $120M. Review clauses.",
    ],
  },
  "HR / Employee Data": {
    color: "#ec4899", bg: "#4a044e",
    prompts: [
      "Employee Tom Baker, salary £85,000, on FMLA since March 2026. Draft his return-to-work plan.",
      "Compensation data: Dev team salaries range £60k–£110k. Write a benchmarking report.",
      "Our employee John Doe has an ADA accommodation for anxiety. Help draft his adjusted schedule.",
      "Performance review for Sarah (employee ID 3312): placed on PIP for attendance. Draft memo.",
    ],
  },
}

interface PromptCard { cat: string; color: string; bg: string; prompt: string }

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5)
}

function generateCards(): PromptCard[] {
  const cats = shuffle(Object.keys(POOL)).slice(0, 6)
  return cats.map(cat => {
    const { color, bg, prompts } = POOL[cat]
    return { cat, color, bg, prompt: prompts[Math.floor(Math.random() * prompts.length)] }
  })
}

function PromptCardItem({ card }: { card: PromptCard }) {
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(card.prompt).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div style={{ background: "#0d1117", border: "1px solid #1f2937", borderRadius: 10, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "1px", padding: "3px 9px", borderRadius: 20, background: card.bg, color: card.color }}>
          {card.cat}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "1px", color: "#E63946", background: "#451a1e", padding: "3px 9px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#E63946", display: "inline-block" }} />
          Should be blocked
        </span>
      </div>
      <div style={{ fontSize: 13, color: "#9ca3af", lineHeight: 1.6, fontFamily: "monospace", background: "#111827", borderRadius: 6, padding: "10px 12px", marginBottom: 8, userSelect: "all" as const }}>
        {card.prompt}
      </div>
      <button onClick={copy} style={{
        background: "transparent", border: `1px solid ${copied ? "#22c55e" : "#1f2937"}`,
        color: copied ? "#22c55e" : "#6b7280", fontSize: 11, padding: "4px 10px",
        borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s",
      }}>
        {copied ? <Check size={11} /> : <Copy size={11} />}
        {copied ? "Copied ✓" : "Copy prompt"}
      </button>
    </div>
  )
}

export default function Beta() {
  const [cards, setCards] = useState<PromptCard[]>([])
  const [spinning, setSpinning] = useState(false)

  useEffect(() => { setCards(generateCards()) }, [])

  function regenerate() {
    setSpinning(true)
    setTimeout(() => { setCards(generateCards()); setSpinning(false) }, 300)
  }

  const S = {
    page:    { minHeight: "100vh", background: "#030712", color: "#d1d5db", fontFamily: "system-ui, sans-serif", padding: "40px 20px" },
    wrap:    { maxWidth: 740, margin: "0 auto" },
    section: { fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "1.5px", color: "#E63946", marginBottom: 16 },
    step:    { display: "flex", gap: 14, alignItems: "flex-start" as const, background: "#0d1117", border: "1px solid #1f2937", borderRadius: 10, padding: "16px 18px" },
    num:     { width: 28, height: 28, borderRadius: "50%", background: "#E63946", color: "#fff", fontSize: 13, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  }

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
          <GuardRokIcon size={32} />
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>
            Guard<span style={{ color: "#E63946" }}>Rok</span>
            <span style={{ background: "#451a1e", color: "#E63946", fontSize: 10, fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "1.5px", padding: "3px 10px", borderRadius: 20, marginLeft: 8, verticalAlign: "middle" }}>Beta</span>
          </div>
        </div>

        {/* Hero */}
        <div style={{ marginBottom: 36 }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: "#fff", lineHeight: 1.3, marginBottom: 10 }}>
            Welcome, Beta Tester 👋<br />Let's make sure you're protected.
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.7 }}>
            This guide walks you through setting up GuardRok and testing it with real prompts. Every time you click "Generate Test Prompts" you'll get a fresh set — try different ones each session.
          </p>
        </div>

        {/* Steps */}
        <div style={S.section}>Step-by-step setup</div>
        <div style={{ display: "flex", flexDirection: "column" as const, gap: 12, marginBottom: 36 }}>
          {[
            {
              n: "1", title: "Install the GuardRok extension",
              body: <>Click <a href={CWS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#E63946" }}>Add to Chrome from the Chrome Web Store</a>. After install, Chrome hides new extensions in the <strong>puzzle piece menu (⊕)</strong> by default — click it, find GuardRok, and click the <strong>pin icon</strong> to show the shield in your toolbar permanently.</>,
              tip: "💡 Don't see the shield after installing? Click the ⊕ puzzle piece icon top-right → pin GuardRok → the red shield will appear.",
            },
            {
              n: "2", title: "Sign in to activate protection",
              body: <>Click the GuardRok shield in your toolbar → enter your work email and password → click <strong>Sign In</strong>. The shield turns <strong style={{ color: "#E63946" }}>red</strong> when you're protected. No account yet? <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#E63946" }}>Sign up at app.guardrok.com</a> or <a href={ALT_URL} target="_blank" rel="noopener noreferrer" style={{ color: "#E63946" }}>tryguardrok.com</a>. If you're already signed into the dashboard, the extension connects automatically.</>,
              tip: null,
            },
            {
              n: "3", title: "Reload your AI tool tab",
              body: <>After installing and signing in, <strong>reload your ChatGPT / Claude / Gemini tab</strong> (press F5 or Ctrl+R). The extension only activates on tabs loaded after install — existing tabs won't be protected until you reload them.</>,
              tip: "💡 Still not blocking after reload? Open the extension popup and confirm the shield is red (signed in). Gray shield = not signed in.",
            },
            {
              n: "4", title: "Open an AI tool",
              body: <>GuardRok works on: <code style={{ background: "#1f2937", color: "#f3f4f6", fontSize: 12, padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>ChatGPT</code> · <code style={{ background: "#1f2937", color: "#f3f4f6", fontSize: 12, padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>Claude.ai</code> · <code style={{ background: "#1f2937", color: "#f3f4f6", fontSize: 12, padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>Gemini</code> · <code style={{ background: "#1f2937", color: "#f3f4f6", fontSize: 12, padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>Copilot</code> · <code style={{ background: "#1f2937", color: "#f3f4f6", fontSize: 12, padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>Perplexity</code> · <code style={{ background: "#1f2937", color: "#f3f4f6", fontSize: 12, padding: "2px 6px", borderRadius: 4, fontFamily: "monospace" }}>Grok</code> and more. Open any of these in your browser.</>,
              tip: null,
            },
            {
              n: "5", title: "Test with the prompts below",
              body: <>Click <strong style={{ color: "#E63946" }}>Generate Test Prompts</strong> to get a fresh set of test prompts. Copy a prompt, paste it into the AI tool, and press <strong>Enter</strong>. GuardRok should block it before it reaches the AI and show you a red modal. ✅<br /><span style={{ fontSize: 12, color: "#6b7280" }}>Note: use the Enter key for testing — send button interception is being rolled out in the next extension update.</span></>,
              tip: null,
            },
          ].map(({ n, title, body, tip }) => (
            <div key={n} style={S.step}>
              <div style={S.num}>{n}</div>
              <div>
                <div style={{ color: "#f3f4f6", fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>{body}</div>
                {tip && <div style={{ background: "#0c1a2e", border: "1px solid #1e3a5f", borderRadius: 8, padding: "10px 14px", marginTop: 8, fontSize: 12, color: "#60a5fa", lineHeight: 1.6 }}>{tip}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* Incognito section */}
        <div style={S.section}>Testing in incognito (private) mode</div>
        <div style={{ background: "#0d1117", border: "1px solid #1f2937", borderRadius: 10, padding: "20px 22px", marginBottom: 36 }}>
          <div style={{ color: "#f3f4f6", fontSize: 15, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={16} color="#f59e0b" /> Enable GuardRok in Incognito mode
          </div>
          <ol style={{ paddingLeft: 20, display: "flex", flexDirection: "column" as const, gap: 6 }}>
            {[
              <>Open a new tab → type <strong>chrome://extensions</strong> → press Enter</>,
              <>Find <strong>GuardRok AI Firewall</strong> → click <strong>Details</strong></>,
              <>Scroll down → toggle <strong>"Allow in Incognito"</strong> → ON</>,
              <>Open a new <strong>Incognito window</strong> (Ctrl+Shift+N / Cmd+Shift+N)</>,
              <>Go to ChatGPT or Claude.ai → paste a test prompt → it should still be blocked ✅</>,
            ].map((step, i) => (
              <li key={i} style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.8 }}>{step}</li>
            ))}
          </ol>
          <div style={{ background: "#0c1a2e", border: "1px solid #1e3a5f", borderRadius: 8, padding: "10px 14px", marginTop: 12, fontSize: 12, color: "#60a5fa", lineHeight: 1.6 }}>
            💡 Enterprise customers: your IT admin can force this on for all devices via Google Admin Console — individual employees don't need to do this step.
          </div>
        </div>

        {/* Prompt generator */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap" as const, gap: 12 }}>
          <div style={S.section as React.CSSProperties & { marginBottom: number }}>Test prompts — click to regenerate</div>
          <button onClick={regenerate} style={{
            background: "#E63946", color: "#fff", border: "none", borderRadius: 8,
            padding: "10px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <RefreshCw size={14} style={{ animation: spinning ? "spin 0.6s linear infinite" : "none" }} />
            Generate Test Prompts
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column" as const, gap: 10, marginBottom: 36 }}>
          {cards.map((card, i) => <PromptCardItem key={i} card={card} />)}
        </div>

        {/* What to expect */}
        <div style={S.section}>What should happen</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 36 }}>
          {[
            { dot: "#E63946", title: "Prompt is blocked", body: "A red GuardRok modal pops up. The prompt never reaches the AI. This is the correct behaviour. Click \"Got it — clear prompt\" or edit your prompt." },
            { dot: "#22c55e", title: "Safe prompts go through", body: "Normal questions with no sensitive data work fine. GuardRok only stops prompts that contain PII, credentials, financial data, or confidential information." },
            { dot: "#E63946", title: "Incognito also blocks", body: "Once you enable \"Allow in Incognito\", the same protection applies in private windows. Switching to incognito should NOT bypass GuardRok." },
            { dot: "#E63946", title: "Sign out = blocked", body: "If you sign out of GuardRok, all AI prompts are blocked until you sign back in. This is intentional — unauthenticated = no access." },
          ].map(({ dot, title, body }) => (
            <div key={title} style={{ background: "#0d1117", border: "1px solid #1f2937", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#f3f4f6", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, display: "inline-block" }} />
                {title}
              </div>
              <p style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center" as const, fontSize: 12, color: "#374151", paddingTop: 24, borderTop: "1px solid #111827" }}>
          Questions? Email{" "}
          <a href="mailto:support@guardrok.com" style={{ color: "#6b7280", textDecoration: "none" }}>support@guardrok.com</a>
          {" · "}
          <Link to="/dashboard" style={{ color: "#6b7280", textDecoration: "none" }}>Open Dashboard</Link>
          {" · "}
          <Link to="/" style={{ color: "#6b7280", textDecoration: "none" }}>guardrok.com</Link>
        </div>

      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
